import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { getId, getToken, usePermissions } from "../../user/User"
import { getStatusOptions } from "../../data/Data"
import { KanbanComponent, ColumnsDirective, ColumnDirective } from "@syncfusion/ej2-react-kanban"
import { registerLicense } from '@syncfusion/ej2-base';
import "@syncfusion/ej2-base/styles/bootstrap5.css"
import '@syncfusion/ej2-buttons/styles/bootstrap5.css'
import "@syncfusion/ej2-layouts/styles/bootstrap5.css"
import '@syncfusion/ej2-dropdowns/styles/bootstrap5.css'
import '@syncfusion/ej2-inputs/styles/bootstrap5.css'
import "@syncfusion/ej2-navigations/styles/bootstrap5.css"
import "@syncfusion/ej2-popups/styles/bootstrap5.css"
import "@syncfusion/ej2-react-kanban/styles/bootstrap5.css"
import axios from "axios"
import md5 from "md5"

/**
 * Documentation: https://ej2.syncfusion.com/react/demos/?utm_source=npm&utm_medium=listing&utm_campaign=react-kanban-npm#/tailwind3/kanban/overview
 */
const ClientBoard = () => {
    const navigate = useNavigate()

    const { permissions } = usePermissions(getId())
    useEffect(() => {
        if(permissions.client !== undefined && !permissions.client) {
            toast.error("You don't have permission to edit clients!")
            navigate("/")
        }
    }, [permissions])
    
    const handleBack = async e => {
        e.preventDefault()
        navigate("/client")
    }

    registerLicense(process.env.REACT_APP_SYNCFUSION_KEY)

    const [data, setData] = useState([])
    useEffect(() => {
        axios.get(process.env.REACT_APP_API_URL + "/api/clients?active=true", {
            headers: {
                "Authorization": "Bearer " + getToken()
            }
        })
        .then(res => {
            if(res.data === null || res.data === "") {
                return
            }
            setData(res.data.map(client => ({
                Id: client.id,
                CompanyName: client.company_name,
                Status: client.status,
                Summary: "Last update: " + (client.days_since_last_update == 0 ? "Today" : client.days_since_last_update + " " + (client.days_since_last_update == 1 ? "day" : "days") + " ago"),
                DaysSinceLastUpdate: client.days_since_last_update,
                Tags: client.tag_names,
                SalesPerson: client.sales_person_mail,
                SalesPersonImage: "https://www.gravatar.com/avatar/" + md5(client.sales_person_mail.toLowerCase()) + "?d=404",
                SalesPersonInitials: client.sales_person_name.split(" ").map(n => n[0]).join("").toUpperCase(),
            })).sort((a, b) => b.DaysSinceLastUpdate - a.DaysSinceLastUpdate)) // Show clients first, which are contacted at least
        }).catch(_ => {
            toast.error("Unable to load clients!")
        })
    }, [])

    async function onDrag(e) {
        const client = e.data[0]
        axios.post(process.env.REACT_APP_API_URL + "/api/clients/status/" + client.Id, JSON.stringify({
            "status": client.Status,
        }), { headers: {
            "Authorization": "Bearer " + getToken()
        }}).then(_ => {
            toast.success("Updated status for client " + client.CompanyName + " (#" + client.Id + ").")
        }).catch(error => {
            if (error.response) {
                var statusCode = error.response.status
                if(statusCode === 400) {
                    toast.error("Invalid request.")
                    return
                } else if(statusCode === 404) {
                    toast.error("Unable to find client #" + client.Id + ".")
                    return
                }
            }
            toast.error("Internal error while loading client status")
        })
    }

    function onCardOpen(client) {
        navigate('/client/' + client.Id);

        // The e-kanban-dialog is hidden. Redirect to the edit page instead of opening the modal
        return <></>
    }

    const cardTemplate = (props) => {
        const [imgError, setImgError] = useState(false);

        return (
            <div className="card-template">
                <div className="card-template-wrap">
                    <table className="card-template-wrap">
                        <tbody>
                            <tr>
                                <td>
                                    <div className="e-card-header">
                                        <h5>{props.CompanyName}</h5>
                                    </div>
                                    <div className="e-card-content">{props.Summary}</div>
                                    <div className="e-card-tags" style={{ marginBottom: "10px" }}>
                                        {props.Tags == null ? "" : props.Tags.map((tag) => ( <div key={tag} className="e-card-tag e-card-label">{tag}</div> ))}
                                    </div>
                                </td>
                                <td style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", padding: "10px" }}>
                                    {!imgError ? (
                                        <img
                                            src={props.SalesPersonImage}
                                            alt={props.SalesPerson}
                                            onError={() => setImgError(true)}
                                            style={{
                                                maxWidth: "40px",
                                                borderRadius: "50%",
                                                backgroundColor: "#ccc",
                                            }}
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                width: "40px",
                                                height: "40px",
                                                borderRadius: "50%",
                                                backgroundColor: "#ccc",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "0.75rem",
                                                fontWeight: "bold",
                                                color: "#333"
                                            }}
                                        >
                                            {props.SalesPersonInitials}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const columnTemplate = (status, props) => {
        return (
            <div className="e-header-wrap">
                <div className="e-header-title">
                    <div className="e-header-text"><i className={status.icon}></i> {status.label}</div>
                    {status.small ? <></> : <div className="e-item-count">- {props.count} clients</div> }
                </div>
            </div>
        );
    };

    return <>
        <button className="btn btn-primary back" onClick={handleBack}>Back to overview</button><br /><br />
        
        <KanbanComponent
            id="kanban"
            keyField="Status"
            dataSource={data}
            cardSettings={{
                headerField: "CompanyName",
                contentField: "Summary",
                tagsField: "Tags",
                template: cardTemplate.bind(this),
            }}
            dialogSettings={{
                template: onCardOpen.bind(this)
            }}
            dragStop={onDrag}
        >
            <ColumnsDirective>
                {getStatusOptions().map((status) => (
                    <ColumnDirective headerText={status.label} keyField={status.value} template={columnTemplate.bind(this, status)} />
                ))}
            </ColumnsDirective>
        </KanbanComponent>
    </>
}
   
export default ClientBoard
